// src/components/ui/table/GenericTableShadcn.jsx
import React from 'react';
import { Trash2 } from 'lucide-react';

// Import shadcn components
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table";
import { Button } from "../button";
import { Skeleton } from "../skeleton";

const GenericTableShadcn = ({
  data = [],
  columns = [],
  loading = false,
  emptyState = null,
  onRowClick = null,
  onDelete = null,
  deleteConfirmMessage = null,
  showActionsColumn = true
}) => {
  // Handle delete with confirmation
  const handleDelete = (e, item) => {
    e.stopPropagation(); // Prevent row click

    const message = deleteConfirmMessage
      ? deleteConfirmMessage(item)
      : 'Are you sure you want to delete this item? This action cannot be undone.';

    if (window.confirm(message)) {
      onDelete(item);
    }
  };

  // Loading skeleton rows
  const LoadingSkeleton = () => (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          {columns.map((col, idx) => (
            <TableCell key={idx}>
              {col.skeleton || <Skeleton className="h-4 w-[120px]" />}
            </TableCell>
          ))}
          {showActionsColumn && onDelete && (
            <TableCell>
              <Skeleton className="h-8 w-8 rounded" />
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  );

  // Empty state
  if (!loading && data.length === 0) {
    const EmptyIcon = emptyState?.icon || null;
    return (
      <div className="text-center py-16">
        {EmptyIcon && <EmptyIcon className="mx-auto h-16 w-16 text-muted-foreground mb-4" />}
        <h3 className="text-xl font-semibold mb-2">
          {emptyState?.title || 'No items yet'}
        </h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          {emptyState?.description || 'Get started by adding your first item.'}
        </p>
        {emptyState?.action && (
          <Button onClick={emptyState.action.onClick}>
            {emptyState.action.icon && <emptyState.action.icon className="mr-2 h-4 w-4" />}
            {emptyState.action.label}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col, idx) => (
              <TableHead
                key={idx}
                className={col.headerClassName}
              >
                {col.header}
              </TableHead>
            ))}
            {showActionsColumn && onDelete && (
              <TableHead className="w-[80px]">Actions</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <LoadingSkeleton />
          ) : (
            data.map((item, rowIdx) => (
              <TableRow
                key={item.id || rowIdx}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                className={onRowClick ? "hover:bg-muted/50 cursor-pointer" : "hover:bg-muted/50"}
              >
                {columns.map((col, colIdx) => (
                  <TableCell
                    key={colIdx}
                    className={col.cellClassName}
                  >
                    {col.render(item)}
                  </TableCell>
                ))}
                {showActionsColumn && onDelete && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDelete(e, item)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default GenericTableShadcn;
